import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useImageProcessing } from '../hooks/useImageProcessing';
import { removeBackgroundFromFile } from '../utils/replicateApi';
import { validateImageFile, uploadToTempStorage } from '../utils/imageUtils';
import { downloadImageFromUrl } from '../utils/imageUtils';
import { useReplicate } from '../hooks/useReplicate';
import ImageDisplay from './ImageDisplay';
import '../styles/FaceUploader.css';

const FaceUploader = ({ onFaceCutoutsReady }) => {
  const [processedFaces, setProcessedFaces] = useState([]);
  const [processingQueue, setProcessingQueue] = useState([]);
  const [removingBg, setRemovingBg] = useState(false);
  const [bgError, setBgError] = useState(null);
  const [progress, setProgress] = useState(null);
  
  const { 
    uploadedImages, 
    processing: uploading, 
    error: uploadError, 
    uploadImage, 
    removeImage,
    clearError: clearUploadError 
  } = useImageProcessing();

  const onDrop = useCallback(async (acceptedFiles) => {
    for (const file of acceptedFiles) {
      try {
        await uploadImage(file);
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }
  }, [uploadImage]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    multiple: true,
    disabled: uploading || removingBg
  });

  const handleRemoveBackground = async (imageData) => {
    if (processingQueue.includes(imageData.id)) return;
    
    try {
      setProcessingQueue(prev => [...prev, imageData.id]);
      setRemovingBg(true);
      setBgError(null);
      setProgress('Removing background...');
      
      // Use the original file object directly
      const result = await removeBackgroundFromFile(imageData.file);
      const cutoutUrl = result;
      
      const processedFace = {
        id: imageData.id,
        originalUrl: imageData.url,
        cutoutUrl,
        name: imageData.name,
        processed: true,
      };
      
      setProcessedFaces(prev => [...prev, processedFace]);
      setProcessingQueue(prev => prev.filter(id => id !== imageData.id));
      
      // Update the parent component
      const allProcessedFaces = [...processedFaces, processedFace];
      onFaceCutoutsReady(allProcessedFaces);
      
    } catch (err) {
      setProcessingQueue(prev => prev.filter(id => id !== imageData.id));
      setBgError(err.message);
      console.error('Background removal failed:', err);
    } finally {
      setRemovingBg(false);
      setProgress(null);
    }
  };

  const handleRemoveFace = (imageId) => {
    removeImage(imageId);
    setProcessedFaces(prev => prev.filter(face => face.id !== imageId));
    setProcessingQueue(prev => prev.filter(id => id !== imageId));
    
    // Update parent component
    const updatedFaces = processedFaces.filter(face => face.id !== imageId);
    onFaceCutoutsReady(updatedFaces);
  };

  const clearBgError = () => {
    setBgError(null);
  };

  const isProcessing = (imageId) => processingQueue.includes(imageId);
  const isProcessed = (imageId) => processedFaces.some(face => face.id === imageId);

  return (
    <div className="face-uploader component-card">
      <h2>Step 3: Upload Guest Faces</h2>
      
      <div className="upload-section">
        <div 
          {...getRootProps()} 
          className={`dropzone ${isDragActive ? 'active' : ''} ${uploading || removingBg ? 'disabled' : ''}`}
        >
          <input {...getInputProps()} />
          <div className="dropzone-content">
            <div className="upload-icon">📸</div>
            {isDragActive ? (
              <p>Drop the face images here...</p>
            ) : (
              <>
                <p>Drag & drop guest face images here, or click to select</p>
                <small>Supports: JPG, PNG, GIF, WebP (max 10MB each)</small>
              </>
            )}
          </div>
        </div>
        
        {(uploadError || bgError) && (
          <div className="error-message">
            {uploadError && (
              <div>
                <p>❌ Upload Error: {uploadError}</p>
                <button onClick={clearUploadError} className="clear-error-btn">
                  Dismiss
                </button>
              </div>
            )}
            {bgError && (
              <div>
                <p>❌ Background Removal Error: {bgError}</p>
                <button onClick={clearBgError} className="clear-error-btn">
                  Dismiss
                </button>
              </div>
            )}
          </div>
        )}
        
        {progress && (
          <div className="progress-indicator">
            <div className="progress-text">{progress}</div>
            <div className="progress-bar">
              <div className="progress-fill"></div>
            </div>
          </div>
        )}
      </div>

      {uploadedImages.length > 0 && (
        <div className="uploaded-images">
          <h3>Uploaded Face Images</h3>
          <div className="images-grid">
            {uploadedImages.map((imageData) => (
              <div key={imageData.id} className="image-item">
                <div className="image-preview">
                  <ImageDisplay 
                    src={imageData.url} 
                    alt={imageData.name}
                    className="face-img"
                  />
                  <button 
                    onClick={() => handleRemoveFace(imageData.id)}
                    className="remove-btn"
                    title="Remove image"
                  >
                    ✕
                  </button>
                </div>
                
                <div className="image-actions">
                  <p className="image-name">{imageData.name}</p>
                  
                  {!isProcessed(imageData.id) && !isProcessing(imageData.id) && (
                    <button 
                      onClick={() => handleRemoveBackground(imageData)}
                      className="process-btn"
                      disabled={removingBg}
                    >
                      Remove Background
                    </button>
                  )}
                  
                  {isProcessing(imageData.id) && (
                    <div className="processing-status">
                      <span>Removing background...</span>
                    </div>
                  )}
                  
                  {isProcessed(imageData.id) && (
                    <div className="processed-status">
                      <span>✅ Background removed</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {processedFaces.length > 0 && (
        <div className="processed-faces">
          <h3>Face Cutouts (Background Removed)</h3>
          <div className="cutouts-grid">
            {processedFaces.map((face) => (
              <div key={face.id} className="cutout-item">
                <ImageDisplay 
                  src={face.cutoutUrl} 
                  alt={`${face.name} cutout`}
                  className="cutout-img"
                />
                <p className="cutout-name">{face.name}</p>
                <div className="face-actions">
                  <button
                    onClick={() => handleRemoveFace(face.id)}
                    className="remove-face-btn"
                    title="Remove this face"
                  >
                    🗑️ Remove
                  </button>
                  <button 
                    onClick={() => downloadImageFromUrl(face.cutoutUrl, `face-cutout-${face.name}.png`)}
                    className="download-face-btn"
                    title="Download face cutout"
                  >
                    💾 Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="help-text">
        <p>👥 Upload images of podcast guests to create face cutouts</p>
        <p>AI will automatically remove backgrounds for clean overlays</p>
        <p>These cutouts will be available for positioning on your thumbnail</p>
      </div>
    </div>
  );
};

export default FaceUploader;
